import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";
import {
  Grid,
  Card,
  Text,
  Group,
  Button,
  Stack,
  Loader,
  Alert,
  Box,
  Title,
  ThemeIcon,
} from "@mantine/core";
import {
  IconUsers,
  IconUserCheck,
  IconClock,
  IconCurrencyDollar,
  IconChartLine,
  IconTrendingUp,
  IconPlus,
  IconRefresh,
  IconAlertCircle,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import RepCardList from "../RepManagement/RepCardList";
import CompanyRevenueTab from "./CompanyRevenueTab";

const VALID_TABS = ["company-users", "company-revenue"];
const DEFAULT_TAB = "company-users";

const getTabFromSearch = search => {
  const tabParam = new URLSearchParams(search).get("tab");
  return VALID_TABS.includes(tabParam) ? tabParam : DEFAULT_TAB;
};

function CompanyUsersPage() {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalReps, setTotalReps] = useState(0);
  const [totalActiveReps, setTotalActiveReps] = useState(0);
  const [totalPendingReps, setTotalPendingReps] = useState(0);
  const [projectedRevenue, setProjectedRevenue] = useState(0);
  const [averageProjectedMargin, setAverageProjectedMargin] = useState(0);
  const [projectedEarnings, setProjectedEarnings] = useState(0);

  // Format currency helper function
  const formatCurrency = value => {
    if (value === null || value === undefined || value === "") return "N/A";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  // Format percentage helper function
  const formatPercentage = value => {
    if (value === null || value === undefined || value === "") return "N/A";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "N/A";
    // Only show decimal if it's not .0
    const formatted =
      numValue % 1 === 0 ? numValue.toFixed(0) : numValue.toFixed(1);
    return formatted + "%";
  };

  const [resendingInvite, setResendingInvite] = useState(null);
  const [showPayDetails] = useState(false);
  const [activeTab, setActiveTab] = useState(() =>
    getTabFromSearch(location.search)
  );

  // Tab definitions
  const TABS = [
    { id: "company-users", label: "Company Users", icon: IconUsers },
    { id: "company-revenue", label: "Company Revenue", icon: IconTrendingUp },
  ];

  // Fetch reps from company API endpoint
  const fetchReps = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Failed to fetch company users");
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/company/reps"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const summary_data = result.data.summary_metrics;
        const reps = result.data?.reps ?? [];
        setReps(reps);
        setTotalReps(summary_data.total_users ?? 0);
        setTotalActiveReps(summary_data.total_active_users ?? 0);
        setTotalPendingReps(summary_data.total_pending_users ?? 0);
        setProjectedRevenue(summary_data.projected_revenue ?? 0);
        setAverageProjectedMargin(summary_data.projected_average_margin ?? 0);
        setProjectedEarnings(summary_data.projected_earnings ?? 0);
        setError(null);
      } else {
        setError("Failed to fetch company users");
      }
    } catch (err) {
      setError("Failed to fetch company users");
    } finally {
      setLoading(false);
    }
  };

  // Resend invite to rep - Note: This might need to be updated with a company-specific endpoint
  const handleResendInvite = async repId => {
    try {
      setResendingInvite(repId);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      // Using the same endpoint as rep management for now - this might need to be changed
      const response = await fetch(getApiEndpoint(`/v1/rep/invite/${repId}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        showSuccess("Invite resent successfully!");
      } else {
        handleApiError(
          new Error(`HTTP ${response.status} - ${response.statusText}`),
          "Failed to resend invite"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to resend invite");
    } finally {
      setResendingInvite(null);
    }
  };

  // Navigate to pitch review page
  const handlePitchReview = pitchReviewChannelId => {
    navigate(`/pitch-review/${pitchReviewChannelId}`);
  };

  useEffect(() => {
    fetchReps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync active tab from the URL query param
  useEffect(() => {
    const tabFromUrl = getTabFromSearch(location.search);
    setActiveTab(prevTab => (prevTab === tabFromUrl ? prevTab : tabFromUrl));
  }, [location.search]);

  // Keep the URL query param in sync with the active tab
  useEffect(() => {
    const currentTabParam = new URLSearchParams(location.search).get("tab");
    if (currentTabParam !== activeTab) {
      navigate(
        {
          pathname: location.pathname,
          search: `?tab=${activeTab}`,
        },
        { replace: true }
      );
    }
  }, [activeTab, location.pathname, location.search, navigate]);

  return (
    <PageContent
      customHeader={
        <div className="org-page-custom-header">
          <div className="org-page-header-left">
            <h1 className="org-page-main-title">Company Downline</h1>
            <nav className="org-page-nav-tabs" role="tablist">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={`org-page-nav-tab${activeTab === tab.id ? " active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      }
    >
      {activeTab === "company-users" && (
        <Stack gap="lg">
          {/* Metrics Cards - First Row */}
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
              <Card padding="lg" radius="md" withBorder h="100%">
                <Group>
                  <ThemeIcon size="lg" variant="light" color="blue">
                    <IconUsers size={20} />
                  </ThemeIcon>
                  <Box style={{ flex: 1 }}>
                    <Text size="sm" color="dimmed" fw={500}>
                      Total Users
                    </Text>
                    <Text size="xl" fw={700}>
                      {totalReps} Users
                    </Text>
                  </Box>
                </Group>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
              <Card padding="lg" radius="md" withBorder h="100%">
                <Group>
                  <ThemeIcon size="lg" variant="light" color="green">
                    <IconUserCheck size={20} />
                  </ThemeIcon>
                  <Box style={{ flex: 1 }}>
                    <Text size="sm" color="dimmed" fw={500}>
                      Active Users
                    </Text>
                    <Text size="xl" fw={700}>
                      {totalActiveReps} Active Users
                    </Text>
                  </Box>
                </Group>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
              <Card padding="lg" radius="md" withBorder h="100%">
                <Group>
                  <ThemeIcon size="lg" variant="light" color="orange">
                    <IconClock size={20} />
                  </ThemeIcon>
                  <Box style={{ flex: 1 }}>
                    <Text size="sm" color="dimmed" fw={500}>
                      Pending Users
                    </Text>
                    <Text size="xl" fw={700}>
                      {totalPendingReps} Pending
                    </Text>
                  </Box>
                </Group>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
              <Card padding="lg" radius="md" withBorder h="100%">
                <Group>
                  <ThemeIcon size="lg" variant="light" color="teal">
                    <IconCurrencyDollar size={20} />
                  </ThemeIcon>
                  <Box style={{ flex: 1 }}>
                    <Text size="sm" color="dimmed" fw={500}>
                      Projected Revenue
                    </Text>
                    <Text size="xl" fw={700}>
                      {formatCurrency(projectedRevenue)}
                    </Text>
                  </Box>
                </Group>
              </Card>
            </Grid.Col>
          </Grid>

          {/* Metrics Cards - Second Row */}
          {showPayDetails && (
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                <Card padding="lg" radius="md" withBorder h="100%">
                  <Group>
                    <ThemeIcon size="lg" variant="light" color="violet">
                      <IconChartLine size={20} />
                    </ThemeIcon>
                    <Box style={{ flex: 1 }}>
                      <Text size="sm" color="dimmed" fw={500}>
                        Average Projected Margin
                      </Text>
                      <Text size="xl" fw={700}>
                        {formatPercentage(averageProjectedMargin)}
                      </Text>
                    </Box>
                  </Group>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                <Card padding="lg" radius="md" withBorder h="100%">
                  <Group>
                    <ThemeIcon size="lg" variant="light" color="indigo">
                      <IconTrendingUp size={20} />
                    </ThemeIcon>
                    <Box style={{ flex: 1 }}>
                      <Text size="sm" color="dimmed" fw={500}>
                        Projected Profit
                      </Text>
                      <Text size="xl" fw={700}>
                        {formatCurrency(projectedEarnings)}
                      </Text>
                    </Box>
                  </Group>
                </Card>
              </Grid.Col>
            </Grid>
          )}

          {/* Company Users Section */}
          <Card padding="lg" radius="md" withBorder>
            <Stack gap="lg">
              <Group justify="space-between" align="center">
                <Title order={3}>Company Users</Title>
                <Group>
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => navigate("/reps/add")}
                  >
                    Add Rep
                  </Button>
                  <Button
                    variant="outline"
                    leftSection={<IconRefresh size={16} />}
                    onClick={fetchReps}
                  >
                    Refresh
                  </Button>
                </Group>
              </Group>

              {loading && (
                <Group justify="center" py="xl">
                  <Loader size="md" />
                  <Text>Loading users...</Text>
                </Group>
              )}

              {error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="Error"
                  color="red"
                >
                  <Group justify="space-between">
                    <Text>{error}</Text>
                    <Button
                      variant="outline"
                      size="sm"
                      leftSection={<IconRefresh size={16} />}
                      onClick={fetchReps}
                    >
                      Retry
                    </Button>
                  </Group>
                </Alert>
              )}

              {!loading && !error && (
                <Box>
                  {reps.length === 0 ? (
                    <Text ta="center" c="dimmed" py="xl">
                      No users found.
                    </Text>
                  ) : (
                    <RepCardList
                      reps={reps}
                      showPayDetails={showPayDetails}
                      onResendInvite={handleResendInvite}
                      onViewDetails={repId => navigate(`/reps/${repId}`)}
                      onViewPayDetails={repId =>
                        navigate(`/user-pay-details/${repId}`)
                      }
                      onPitchReview={handlePitchReview}
                      resendingInvite={resendingInvite}
                    />
                  )}
                </Box>
              )}
            </Stack>
          </Card>
        </Stack>
      )}

      {activeTab === "company-revenue" && <CompanyRevenueTab />}
    </PageContent>
  );
}

export default CompanyUsersPage;
